/**
 * Group M — CRM pipelines. The backend twin is `api/v1/pipelines.py`; the serializers
 * there are the source of truth for these wire shapes.
 *
 * TWO KINDS OF BOARD, and everything here follows from which one you are holding.
 *
 * A BUILT-IN is computed. `domain.crm.engine` resolves every non-staff member into
 * exactly one state each tick, so membership is not something a client edits — the only
 * writes are a manual `move` (which pins the card for 7d so the resolver leaves the
 * correction alone) and `unpin`.
 *
 * A CUSTOM board is authored by the owner and filled by hand. The engine skips it
 * entirely, which is what makes `createPipeline`/`updatePipeline`/`addPipelineCard`/
 * `removePipelineCard` mean anything: nothing recomputes them away. `Pipeline.manual`
 * is the flag; the server derives it so no client re-derives it from `kind`.
 *
 * WHY THE FETCHERS NORMALIZE. The wire member is §2.1's canonical `Member` with its
 * `membership` and `stats` sub-blocks; the card tile, the drawer, `PlanCell` and
 * `RenewalCell` all want the four billing columns + `stage`/`last_active` flattened
 * onto one object (which is also what makes it satisfy `MemberPlan`/`MemberRenewal`
 * structurally). That flattening happens here, at the one boundary that knows the
 * wire, exactly as `@api/v1/pipelines` does for the queue row.
 */

import { apiDelete, apiGet, apiPatch, apiPost } from './http';
import { community } from './envelope';
import type { ListEnvelope } from './envelope';
import type { MemberStage, MemberStatus, PlanInterval } from './shapes';

import { journeySegments, type JourneyStep } from './members';
import type { RoleJourneySegment } from './shapes';

// ---------------------------------------------------------------------------
// The wire (mirrors `api/v1/pipelines.py`'s response models)
// ---------------------------------------------------------------------------

interface MembershipWire {
  status?: string | null;
  plan_currency?: string | null;
  plan_amount_cents?: number | null;
  plan_interval?: string | null;
  plan_tier?: string | null;
  ends_at?: string | null;
  cancelled_at?: string | null;
  declined_at?: string | null;
  departed_at?: string | null;
  ltv_cents?: number | null;
}

interface MemberStatsWire {
  stage?: string | null;
  momentum?: number | null;
  last_active?: string | null;
  first_active?: string | null;
}

/** §2.1's canonical member — identity plus the optional billing/contribution blocks. */
interface MemberWire {
  member_id: number;
  external_id?: string | null;
  handle?: string | null;
  name?: string | null;
  picture_profile?: string | null;
  joined_at?: string | null;
  membership?: MembershipWire | null;
  stats?: MemberStatsWire | null;
  /** §2.1's role-journey block — the same one `/v1/members` returns, rendered by the
   *  same `<RoleJourney>` the members table and the hovercard use. */
  journey?: JourneyStep[] | null;
  activity?: { weeks?: { week: string; total: number }[]; max?: number } | null;
  survey?: { question: string; type?: string | null; answer?: string | null }[] | null;
}

interface StageWire {
  key: string;
  label: string;
  /** `in_progress` | `won` | `lost`. */
  kind: string;
  /** The board does not draw this column. See `PipelineStage.hidden`. */
  hidden?: boolean;
  /** This column cannot fill without a roster sweep. See `PipelineStage.needs_roster`. */
  needs_roster?: boolean;
}

interface PipelineWire {
  key: string;
  name: string;
  /** The pipeline category — `engagement` | `membership` | `churn` | `newcomer` |
   *  a custom kind. Distinct from a STAGE's kind. */
  kind: string;
  enabled: boolean;
  sort_order: number;
  stages?: StageWire[];
  /** Authored by an owner and filled BY HAND — the engine never touches its cards. */
  manual?: boolean;
}

interface PipelineListWire extends ListEnvelope<PipelineWire> {}

interface CardWire {
  card_id: number;
  pipeline_key: string;
  member_id: number;
  stage_key: string;
  outcome: string;
  entered_at?: string | null;
  stage_changed_at?: string | null;
  stage_changed_by?: string | null;
  pinned_until?: string | null;
  note?: string | null;
  note_updated_at?: string | null;
  note_updated_by?: string | null;
  member?: MemberWire | null;
}

interface StageColumnWire extends StageWire {
  total?: number;
  cards?: CardWire[];
  /** The state's DECLARED ordering (`definition.stages[].sort`) — what the board
   *  rendered this column in, and the default a re-sort control starts from. */
  sort?: string | null;
}

interface BoardWire {
  pipeline: PipelineWire;
  stages?: StageColumnWire[];
  total?: number;
}

interface CardPageWire {
  items: CardWire[];
  total: number;
  state: StageWire;
}

interface TransitionWire {
  from_state?: string | null;
  to_state: string;
  actor: string;
  occurred_at?: string | null;
  event?: string | null;
  reason?: string | null;
  why?: TransitionWhy | null;
}

interface HistoryWire {
  items: TransitionWire[];
  total: number;
}

// ---------------------------------------------------------------------------
// Component-facing types
// ---------------------------------------------------------------------------

export type StageKind = 'in_progress' | 'won' | 'lost';

export interface PipelineStage {
  key: string;
  label: string;
  kind: StageKind;
  /** Not drawn on the board. A SCOPED pipeline (Engagement) still resolves every
   *  member — the card exists and per-member reads find it — but its catch-all holds
   *  the population that board is not about, and drawing tens of thousands of cards
   *  nobody works is worse than leaving the column out. */
  hidden: boolean;
  /** This column can only fill for a community whose roster we can read — `Departed`
   *  reads a membership status that is null without a staff role, and `Joined` holds
   *  members we never discover without one. The board pairs it with the community's
   *  `roster_state` to explain the emptiness rather than look broken. */
  needs_roster: boolean;
}

export interface Pipeline {
  key: string;
  name: string;
  kind: string;
  enabled: boolean;
  sort_order: number;
  stages: PipelineStage[];
  /** A board the owner authored, populated by hand. The engine never materialises,
   *  moves or reaps its cards, which is what makes "add a member" and "remove this
   *  card" mean anything here and nothing on a computed board. Gates the whole manual
   *  affordance set in the UI. */
  manual: boolean;
}

/** One column as the editor submits it. `key` is echoed back for a column that already
 *  exists so the server reads an edit as a RENAME rather than a delete plus an insert,
 *  which would strand every card filed in it. Omitted for a newly added column. */
export interface StageDraft {
  key?: string;
  label: string;
  kind: StageKind;
}

/** The card's subject, flattened so it satisfies `MemberPlan` and `MemberRenewal`
 *  structurally — `PlanCell` and `RenewalCell` take it directly. Null when a
 *  re-scrape dropped the member row; the card still renders (as unknown) and stays
 *  movable, exactly as the win-back board relies on. */
/** One membership question and what the member typed at the door. */
export interface SurveyAnswer {
  question: string;
  type: string | null;
  /** Null when unanswered — or, for an `email` question, when Skool withheld it because
   *  we are only a moderator. `type === 'email' && answer === null` is that case. */
  answer: string | null;
}

export interface PipelineMember {
  member_id: number;
  external_id: string;
  handle: string | null;
  name: string;
  picture_profile: string | null;
  // MemberPlan
  plan_currency: string | null;
  plan_amount_cents: number | null;
  plan_interval: PlanInterval | null;
  plan_tier: string | null;
  /** What they have actually paid, to date, in cents (Skool's lifetime spend). Null
   *  where the members page is — no staff role, or one of the tabs that carries no
   *  billing block. Distinct from `plan_amount_cents`, which is the recurring price. */
  ltv_cents: number | null;
  status: MemberStatus | null;
  // MemberRenewal
  ends_at: string | null;
  /** When they hit cancel. They are still a member — and still paying — until
   *  `ends_at`, which is exactly why the card has to distinguish the two. */
  cancelled_at: string | null;
  /** When their PAYMENT FAILED. Not a cancellation: they never chose to leave, and the
   *  fix is a new card rather than a win-back. Skool files both on one tab, so a client
   *  reading only `cancelled_at` reports a declined member as having quit. */
  declined_at: string | null;
  departed_at: string | null;
  // contribution
  stage: MemberStage | null;
  momentum: number | null;
  last_active: string | null;
  first_active: string | null;
  /** The member's contributor-role arc, oldest segment to newest, ready for
   *  `<RoleJourney>`. Empty where we have no journey for them. */
  journey: RoleJourneySegment[];
  /** When Skool approved their membership. NULL where we hold no staff role and
   *  cannot read the roster — the card falls back to `first_active` and says so. */
  joined_at: string | null;
  /** 53 weekly contribution buckets for the activity strip; empty when unknown. */
  activity_weeks: { week: string; total: number }[];
  activity_max: number;
  /** What they answered at the door. DETAIL ONLY — served by `GET …/cards/{id}`, the
   *  drawer's read, and absent from the board, where three answers a member across a
   *  175-card payload would be prose nobody asked for. Empty array when the community
   *  asks no join questions.
   *
   *  An `email` answer is EMPTY unless we hold admin or owner: Skool blanks every field
   *  it recognises as an address below that role. The question still arrives, so the
   *  client can say why the answer is missing instead of showing a blank. */
  survey: SurveyAnswer[];
}

export interface PipelineCard {
  card_id: number;
  pipeline_key: string;
  member_id: number;
  stage_key: string;
  /** `open` | `won` | `lost`. */
  outcome: string;
  entered_at: string | null;
  stage_changed_at: string | null;
  stage_changed_by: string | null;
  pinned_until: string | null;
  /** THE OWNER'S WORKING NOTE on this card. Served on the board LIST as well as on the
   *  detail, unlike `members.contact_*` — a phone number is looked up about one person,
   *  but a note is what you scan a board for, and one hidden behind a click is one
   *  nobody reads. The tile clamps it; the drawer shows all of it. */
  note: string | null;
  note_updated_at: string | null;
  note_updated_by: string | null;
  member: PipelineMember | null;
}

export interface PipelineColumn extends PipelineStage {
  /** How many cards are in this state IN TOTAL — not `cards.length`. Every member of
   *  the community holds a card in every pipeline, so a column can run to tens of
   *  thousands; `cards` is only the page fetched so far. */
  total: number;
  cards: PipelineCard[];
  /** The ordering this column arrived in — the state's own declaration. A reader's
   *  override lives on the page, not here, so this stays "what the board designed". */
  sort: string | null;
}

export interface PipelineBoardData {
  pipeline: Pipeline;
  stages: PipelineColumn[];
  /** Cards across every state — i.e. the community's non-staff member count. */
  total: number;
}

/** One recorded state change. `from_state` is null on a card's first row: that is
 *  materialisation (the member entering the pipeline), not a move. */
/**
 * Why the RESOLVER made a move: the state that claimed the member, the predicate it
 * matched, and what each signal in that predicate read for them at that moment.
 *
 * `signals` is deliberately open. The registry grows, so a client that switched on a
 * fixed set of names would start dropping the very reading that explains a surprising
 * move — render whatever arrives, keyed by name.
 */
export interface TransitionWhy {
  /** The state key that claimed them — the same key as `to_state` on a move. */
  state?: string | null;
  /** The condition tree that matched. Machine-shaped; shown raw only on demand. */
  when?: unknown;
  /** `{signal: the value this member read}` — number, boolean, string or ISO stamp. */
  signals?: Record<string, string | number | boolean | null> | null;
}

export interface CardTransition {
  from_state: string | null;
  to_state: string;
  actor: string;
  occurred_at: string | null;
  /** `move` | `pin` | `unpin`. The last two carry `from_state === to_state` because
   *  nothing moved — reading this is how they are told apart from a no-op move. */
  event: string;
  /** The human's stated reason. Null on every row the resolver wrote. */
  reason: string | null;
  /** The MACHINE's reason, and the mirror of `reason`: null on every human row, and on
   *  rows written before the resolver started recording it. */
  why: TransitionWhy | null;
}

// ---------------------------------------------------------------------------
// Wire → component types
// ---------------------------------------------------------------------------

function toStageKind(kind: string | null | undefined): StageKind {
  return kind === 'won' || kind === 'lost' ? kind : 'in_progress';
}

function toStage(w: StageWire): PipelineStage {
  return {
    key: w.key,
    label: w.label || w.key,
    kind: toStageKind(w.kind),
    hidden: w.hidden ?? false,
    needs_roster: w.needs_roster ?? false,
  };
}

function toPipeline(w: PipelineWire): Pipeline {
  return {
    key: w.key,
    name: w.name,
    kind: w.kind,
    enabled: w.enabled,
    sort_order: w.sort_order,
    stages: (w.stages ?? []).map(toStage),
    manual: w.manual ?? false,
  };
}

function toMember(src: MemberWire | null | undefined): PipelineMember | null {
  if (!src) return null;
  const m = src.membership ?? {};
  const s = src.stats ?? {};
  return {
    member_id: src.member_id,
    external_id: src.external_id ?? '',
    handle: src.handle ?? null,
    name: src.name ?? 'Unknown member',
    picture_profile: src.picture_profile ?? null,
    plan_currency: m.plan_currency ?? null,
    plan_amount_cents: m.plan_amount_cents ?? null,
    plan_interval: (m.plan_interval ?? null) as PlanInterval | null,
    plan_tier: m.plan_tier ?? null,
    ltv_cents: m.ltv_cents ?? null,
    status: (m.status ?? null) as MemberStatus | null,
    ends_at: m.ends_at ?? null,
    cancelled_at: m.cancelled_at ?? null,
    declined_at: m.declined_at ?? null,
    departed_at: m.departed_at ?? null,
    stage: (s.stage ?? null) as MemberStage | null,
    momentum: s.momentum ?? null,
    last_active: s.last_active ?? null,
    first_active: s.first_active ?? null,
    journey: journeySegments(src.journey ?? undefined),
    joined_at: src.joined_at ?? null,
    activity_weeks: src.activity?.weeks ?? [],
    activity_max: src.activity?.max ?? 0,
    survey: (src.survey ?? []).map((a) => ({
      question: a.question,
      type: a.type ?? null,
      answer: a.answer ?? null,
    })),
  };
}

function toCard(w: CardWire): PipelineCard {
  return {
    card_id: w.card_id,
    pipeline_key: w.pipeline_key,
    member_id: w.member_id,
    stage_key: w.stage_key,
    outcome: w.outcome,
    entered_at: w.entered_at ?? null,
    stage_changed_at: w.stage_changed_at ?? null,
    stage_changed_by: w.stage_changed_by ?? null,
    pinned_until: w.pinned_until ?? null,
    note: w.note ?? null,
    note_updated_at: w.note_updated_at ?? null,
    note_updated_by: w.note_updated_by ?? null,
    member: toMember(w.member),
  };
}

function toColumn(w: StageColumnWire): PipelineColumn {
  const cards = (w.cards ?? []).map(toCard);
  // `total` is authoritative; fall back to the page length only if the server omitted
  // it, so a column never claims to be smaller than what it is already showing.
  return { ...toStage(w), total: w.total ?? cards.length, cards, sort: w.sort ?? null };
}

function toTransition(w: TransitionWire): CardTransition {
  return {
    from_state: w.from_state ?? null,
    to_state: w.to_state,
    actor: w.actor,
    occurred_at: w.occurred_at ?? null,
    event: w.event || 'move',
    reason: w.reason ?? null,
    why: w.why ?? null,
  };
}

// ---------------------------------------------------------------------------
// The routes
// ---------------------------------------------------------------------------

/** `GET /v1/communities/{skool_id}/pipelines` — the community's effective pipelines
 *  (built-in templates + its own overrides, disabled rows dropped), each with its
 *  stage list, in the engine's pass order. The switcher renders these. */
export async function fetchPipelines(skoolId: number, includeArchived = false): Promise<Pipeline[]> {
  const body = await apiGet<PipelineListWire>(community(skoolId, '/pipelines'),
    includeArchived ? { include_archived: true } : {});
  return body.items.map(toPipeline);
}

/** `GET /v1/communities/{skool_id}/pipelines/{key}/board` — one pipeline's states,
 *  each with its TOTAL card count and a capped preview page of cards.
 *
 *  There is no queue: pipelines are total state machines, so every member already
 *  holds a card in some state. A column's `total` can be tens of thousands — page the
 *  rest through `fetchPipelineStateCards`.
 *
 *  `q` filters EVERY column by member name or handle, counts included, which is how the
 *  board answers "where is this member". `paying` narrows to members on a paid plan and
 *  composes with it. Pass the SAME `q` and `paying` to `fetchPipelineStateCards` when
 *  paging a filtered column — its offsets are computed against the filtered result, so an
 *  unfiltered page 2 would skip and duplicate. */
export async function fetchPipelineBoard(
  skoolId: number,
  key: string,
  opts: { q?: string; paying?: boolean; segment?: string } = {},
): Promise<PipelineBoardData> {
  const p = new URLSearchParams();
  const term = opts.q?.trim();
  if (term) p.set('q', term);
  // Only ever sent when ON — the server's default is off, and an explicit `paying=false`
  // on every unfiltered board load would make the URL lie about being filtered.
  if (opts.paying) p.set('paying', 'true');
  // A revenue segment from the Overview tiles. Same rule as `q` and `paying`: it reaches
  // the per-state counts as well as the previews, so a filtered column never heads an
  // unfiltered total — and the same rule for paging, below.
  if (opts.segment) p.set('segment', opts.segment);
  const qs = p.toString();
  const body = await apiGet<BoardWire>(
    community(skoolId, `/pipelines/${key}/board`) + (qs ? `?${qs}` : ''),
  );
  const stages = (body.stages ?? []).map(toColumn);
  return {
    pipeline: toPipeline(body.pipeline),
    stages,
    total: body.total ?? stages.reduce((n, c) => n + c.total, 0),
  };
}

/** `GET …/pipelines/{key}/states/{state_key}/cards` — one state column, paged.
 *
 *  This is how a column is read past the board's preview. The ordering defaults to the
 *  state's own — the same declaration `board` used — so a plain page continues the
 *  preview it follows rather than reshuffling it. `q` filters on member name or handle;
 *  `sort` re-orders THIS column only.
 *
 *  KEEP SENDING BOTH WHILE PAGING. `offset` counts rows in the filtered, ordered result,
 *  so dropping either on page 2 skips and duplicates rather than merely looking odd. */
export async function fetchPipelineStateCards(
  skoolId: number,
  key: string,
  stateKey: string,
  opts: {
    offset?: number;
    limit?: number;
    q?: string;
    paying?: boolean;
    segment?: string;
    sort?: string | undefined;
  } = {},
): Promise<{ cards: PipelineCard[]; total: number }> {
  const p = new URLSearchParams();
  if (opts.offset) p.set('offset', String(opts.offset));
  if (opts.limit) p.set('limit', String(opts.limit));
  if (opts.q?.trim()) p.set('q', opts.q.trim());
  if (opts.paying) p.set('paying', 'true');
  // MUST match the board's segment. These offsets count rows in the FILTERED column, so
  // a page 2 fetched without it would skip and duplicate — the same trap `q` documents.
  if (opts.segment) p.set('segment', opts.segment);
  if (opts.sort) p.set('sort', opts.sort);
  const qs = p.toString();
  const body = await apiGet<CardPageWire>(
    community(skoolId, `/pipelines/${key}/states/${stateKey}/cards`) + (qs ? `?${qs}` : ''),
  );
  return { cards: (body.items ?? []).map(toCard), total: body.total ?? 0 };
}

/** `POST …/pipelines/{key}/cards/{card_id}/move` — manually move a card to
 *  `stageKey`. The server sets `stage_changed_by` to the admin and pins the card for
 *  7 days (so the engine won't yank the correction back); a move into a terminal
 *  stage closes it. Returns the updated card.
 *
 *  `reason` is REQUIRED and refused blank (`422 validation_failed`, `parameter:
 *  "reason"`). It is stored on the history row, which is the only place a later reader
 *  can find out why a card contradicts the resolver. Moving a card to the state it is
 *  already in is legal — it re-pins for another 7 days and logs `event: 'pin'`. */
export function movePipelineCard(
  skoolId: number,
  key: string,
  cardId: number,
  stageKey: string,
  reason: string,
): Promise<PipelineCard> {
  return apiPost<CardWire>(
    community(skoolId, `/pipelines/${key}/cards/${cardId}/move`),
    { stage_key: stageKey, reason },
  ).then(toCard);
}

/** `POST …/pipelines/{key}/cards/{card_id}/unpin` — release a manual pin early, handing
 *  the card back to the resolver, which may move it straight back on the next tick.
 *
 *  `reason` is required on the same terms as `move`. Unpinning a card that is not pinned
 *  is `409 not_pinned` rather than a silent success — it means the view is stale. */
export function unpinPipelineCard(
  skoolId: number,
  key: string,
  cardId: number,
  reason: string,
): Promise<PipelineCard> {
  return apiPost<CardWire>(
    community(skoolId, `/pipelines/${key}/cards/${cardId}/unpin`),
    { reason },
  ).then(toCard);
}

/** `GET …/pipelines/{key}/cards/{card_id}` — one card for the drawer: its state and
 *  the timestamps that make up its history. */
export function fetchPipelineCard(
  skoolId: number,
  key: string,
  cardId: number,
): Promise<PipelineCard> {
  return apiGet<CardWire>(
    community(skoolId, `/pipelines/${key}/cards/${cardId}`),
  ).then(toCard);
}

/** `GET …/pipelines/{key}/cards/{card_id}/history` — this card's recorded state
 *  changes, newest first. Cards are permanent and hold only the current state, so the
 *  journey lives here. */
export async function fetchCardHistory(
  skoolId: number,
  key: string,
  cardId: number,
  limit = 50,
): Promise<{ items: CardTransition[]; total: number }> {
  const body = await apiGet<HistoryWire>(
    community(skoolId, `/pipelines/${key}/cards/${cardId}/history`) + `?limit=${limit}`,
  );
  return { items: (body.items ?? []).map(toTransition), total: body.total ?? 0 };
}

// ---------------------------------------------------------------------------
// Authoring a board of your own
//
// Only a CUSTOM pipeline answers to any of these. The three built-ins are computed by
// the tick from a member's signals, so editing their columns or hand-filing their cards
// would be undone within the hour — the server answers `409 pipeline_not_editable`
// rather than accepting a write it is about to overwrite.
// ---------------------------------------------------------------------------

/** Author a manual board. Created enabled and empty. */
export async function createPipeline(
  skoolId: number,
  name: string,
  stages: StageDraft[],
): Promise<Pipeline> {
  const w = await apiPost<PipelineWire>(community(skoolId, '/pipelines'), { name, stages });
  return toPipeline(w);
}

/** Rename a board, rewrite its columns, or archive/restore it. Every field is optional:
 *  a rename must not require restating the columns. Dropping a column that still holds
 *  cards is refused with `409 state_not_empty` rather than stranding them. */
export async function updatePipeline(
  skoolId: number,
  key: string,
  patch: { name?: string; stages?: StageDraft[]; enabled?: boolean },
): Promise<Pipeline> {
  const w = await apiPatch<PipelineWire>(
    community(skoolId, `/pipelines/${encodeURIComponent(key)}`), patch);
  return toPipeline(w);
}

/** Take a board off the switcher. ARCHIVES rather than destroys — the cards and their
 *  whole history survive, and `updatePipeline(..., { enabled: true })` brings it back.
 *  Fetch the list with `includeArchived` to find one again. */
export async function archivePipeline(skoolId: number, key: string): Promise<Pipeline> {
  const w = await apiDelete<PipelineWire>(
    community(skoolId, `/pipelines/${encodeURIComponent(key)}`));
  return toPipeline(w);
}

/** Put a member on a manual board. `409 already_on_board` if they are already on it. */
export async function addPipelineCard(
  skoolId: number,
  key: string,
  memberId: number,
  stageKey: string,
  reason?: string,
): Promise<PipelineCard> {
  const w = await apiPost<CardWire>(
    community(skoolId, `/pipelines/${encodeURIComponent(key)}/cards`),
    { member_id: memberId, stage_key: stageKey, reason },
  );
  return toCard(w);
}

/** Take a card off a manual board, with its history. A real delete — safe here only
 *  because no engine will put the member back on the next tick. */
export function removePipelineCard(
  skoolId: number,
  key: string,
  cardId: number,
): Promise<void> {
  return apiDelete<void>(
    community(skoolId, `/pipelines/${encodeURIComponent(key)}/cards/${cardId}`),
  );
}

/** Write the owner's working note on a card. `null` (or a blank string) clears it.
 *
 *  WORKS ON EVERY PIPELINE, built-in included — unlike add/remove, which only a manual
 *  board can answer. The engine re-files cards by signal and has no opinion about text,
 *  so a note survives every move the tick makes and travels with the card into its new
 *  column. No `reason` is required: a note IS the reason. */
export async function setCardNote(
  skoolId: number,
  key: string,
  cardId: number,
  note: string | null,
): Promise<PipelineCard> {
  const w = await apiPatch<CardWire>(
    community(skoolId, `/pipelines/${encodeURIComponent(key)}/cards/${cardId}/note`),
    { note },
  );
  return toCard(w);
}
